/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import _ from 'lodash';
import { fromObjectEntries, objectEntries } from '../utils';
export const MAX_ESS_COUNT = 5;
export const MAX_THERMAL_COUNT = 50;
export var MerchantPriceOptions;
(function (MerchantPriceOptions) {
    MerchantPriceOptions["MARKET_PRICES"] = "Market Prices";
    MerchantPriceOptions["MULTI_YEAR_PRICES"] = "Multi-Year Prices";
    MerchantPriceOptions["PRICE_CRACK"] = "Price Crack";
})(MerchantPriceOptions || (MerchantPriceOptions = {}));
export var OperationType;
(function (OperationType) {
    OperationType["PRICE_DISPATCH"] = "Price Dispatch";
    OperationType["FIXED_SHAPE"] = "Fixed Shape";
    OperationType["SHAPE_WITH_PRICE_DISPATCH"] = "Fixed Shape with Price Dispatch";
    OperationType["AS_AVAIL_WO_CLIP_CAPTURE"] = "As Available without Clipping Capture";
    OperationType["AS_AVAIL_W_CLIP_CAPTURE"] = "As Available with Clipping Capture";
})(OperationType || (OperationType = {}));
export var Metrics;
(function (Metrics) {
    Metrics["poi_plant_out"] = "poi.plant_out";
    Metrics["poi_power_in"] = "poi.power_in";
    Metrics["poi_power_out"] = "poi.power_out";
    Metrics["poi_clipped"] = "poi.clipped";
    Metrics["shape_profile"] = "shape.profile";
    Metrics["shape_excess"] = "shape.excess";
    Metrics["shape_deficit"] = "shape.deficit";
    Metrics["shape_delivery"] = "shape.delivery";
    Metrics["shape_50_pct_deficit"] = "shape.50_pct_deficit";
    Metrics["shape_90_pct_deficit"] = "shape.90_pct_deficit";
    Metrics["shape_normalized_delivery"] = "shape.normalized_delivery";
    Metrics["shape_normalized_deficit"] = "shape.normalized_deficit";
    Metrics["shape_deficit_cost"] = "shape.deficit_cost";
    Metrics["shape_excess_revenue"] = "shape.excess_revenue";
    Metrics["shape_buy_price"] = "shape.buy_price";
    Metrics["shape_sell_price"] = "shape.sell_price";
    Metrics["price_dispatch_sell_price"] = "price_dispatch.sell_price";
    Metrics["price_dispatch_buy_price"] = "price_dispatch.buy_price";
    Metrics["price_dispatch_sell_power"] = "price_dispatch.sell_power";
    Metrics["price_dispatch_buy_power"] = "price_dispatch.buy_power";
    Metrics["price_dispatch_revenue"] = "price_dispatch.revenue";
    Metrics["pv_power_out"] = "pv.power_out";
    Metrics["wind_power_out"] = "wind.power_out";
    Metrics["bess_charge"] = "bess-1.charge";
    Metrics["bess_discharge"] = "bess-1.discharge";
})(Metrics || (Metrics = {}));
export var ShapeObjectives;
(function (ShapeObjectives) {
    ShapeObjectives["minimize_deficit"] = "Minimize Deficit";
    ShapeObjectives["minimize_cost"] = "Minimize Net Cost";
})(ShapeObjectives || (ShapeObjectives = {}));
export const netBenefitColumnsLabelMap = {
    scenario: { label: 'Scenario #', customUnit: 'index' },
    ic_size_mw: { label: 'IC size [MW]' },
    pv_ac_mwac: { label: 'PV AC [MWac]' },
    pv_ac_inverters: { label: 'PV AC Inverters [MWac]' },
    pv_ac_poi: { label: 'PV AC POI [MWac]' },
    pv_dc_mwdc: { label: 'PV DC [MWdc]' },
    wind_mwac: { label: 'Wind [MWac]' },
    ess_power_mw_deprecated: { label: 'ESS power [MW]' },
    ess_duration_hr_deprecated: { label: 'ESS duration [hr]' },
    ...fromObjectEntries(_.range(1, MAX_ESS_COUNT + 1).flatMap((i) => [
        [`ess_${i}_power_mw`, { label: `ESS ${i} power [MW]` }],
        [`ess_${i}_duration_hr`, { label: `ESS ${i} duration [hr]` }],
        [`ess_${i}_energy`, { label: `ESS ${i} energy [MWh]` }],
    ])),
    ...fromObjectEntries(_.range(1, MAX_THERMAL_COUNT + 1).map((i) => [
        `thermal_${i}_capacity_mw`,
        { label: `Thermal ${i} Capacity [MW]` },
    ])),
    ...fromObjectEntries(_.range(1, MAX_THERMAL_COUNT + 1).map((i) => [
        `thermal_${i}_gen_mwh`,
        { label: `Thermal ${i} Gen [MWh]` },
    ])),
    ...fromObjectEntries(_.range(1, MAX_ESS_COUNT + 1).flatMap((i) => [
        [`charge_${i}`, { label: `Charge ${i} [MWh]` }],
        [`discharge_${i}`, { label: `Discharge ${i} [MWh]` }],
    ])),
    charge_deprecated: { label: 'Charge [MWh]' },
    discharge_deprecated: { label: 'Discharge [MWh]' },
    ess_energy_deprecated: { label: 'ESS energy [MWh]' },
    poi_clipping_mwh: { label: 'POI Clipping [MWh]' },
    poi_clipping_perc: { label: 'POI Clipping [%]', customUnit: '%' },
    delivery_mwh: { label: 'Delivery [MWh]' },
    peak_shape: { label: 'Peak Shape [MW]', customUnit: 'MW' },
    total_shape: { label: 'Total Shape [MWh]' },
    total_annual_load_match: { label: 'Total Annual Load Match [%]', customUnit: '%' },
    annual_shape_match: { label: 'Annual Shape Match [%]', customUnit: '%' },
    annual_renewable_shape_match: { label: 'Annual Renewable Shape Match [%]', customUnit: '%' },
    shape_met: { label: 'Shape Met [%]', customUnit: '%' },
    full_shape_met_frequency: { label: 'Full Shape Met Frequency [%]', customUnit: '%' },
    excess_deprecated: { label: 'Excess [MWh]' },
    deficit_deprecated: { label: 'Deficit [MWh]' },
    excess: { label: 'Excess [%]', customUnit: '%' },
    deficit: { label: 'Deficit [%]', customUnit: '%' },
    npv_net_benefit: { label: 'NPV Net Benefit [$]', customUnit: '$' },
    irr_perc: { label: 'IRR [%]', customUnit: '%' },
    ppa_price: { label: 'Project PPA [$/MWh] or Capacity [$/MW/mo] Price', customUnit: '$' },
    ppa_price_yearly: {
        label: 'Project PPA [$/MWh] or Capacity [$/MW/mo] Price Year',
        customUnit: '$',
    },
    shape_adjusted_project_ppa_price: {
        label: 'Shape Adjusted Project PPA Price [$/MWh]',
        customUnit: '$',
    },
    shape_adjusted_project_ppa_price_yearly: {
        label: 'Shape Adjusted Project PPA Price [$/MWh] Year',
        customUnit: '$',
    },
    load_ppa_price_deprecated: {
        label: 'Load PPA [$/MWh] or Capacity [$/MW/mo] Price',
        customUnit: '$',
    },
    load_ppa_price: { label: 'Load PPA Price [$/MWh]', customUnit: '$' },
    load_ppa_price_yearly: { label: 'Load PPA Price [$/MWh] Year', customUnit: '$' },
    capex_after_itc: { label: 'Capex (after ITC) [$]', customUnit: '$' },
    net_benefit: { label: 'Net Benefit [$]', customUnit: '$' },
    total_revenue: { label: 'Total Revenue [$]', customUnit: '$' },
    npv_total_revenue: { label: 'NPV Total Revenue [$]', customUnit: '$' },
    market_net_revenue: { label: 'Market Net Revenue [$]', customUnit: '$' },
    value_of_delivery: { label: 'Value of Delivery/Shape [$]', customUnit: '$' },
    excess_revenue: { label: 'Excess Revenue [$]', customUnit: '$' },
    deficit_cost: { label: 'Deficit Cost [$]', customUnit: '$' },
    ptc_revenue: { label: 'PTC Revenue [$]', customUnit: '$' },
    rec_revenue: { label: 'REC Revenue [$]', customUnit: '$' },
    capacity_revenue: { label: 'Capacity Revenue [$]', customUnit: '$' },
    total_cost: { label: 'Total Cost [$]', customUnit: '$' },
    npv_total_cost: { label: 'NPV Total Cost [$]', customUnit: '$' },
    pv_capex_deprecated: { label: 'PV Capex [$]', customUnit: '$' },
    pv_capex_ac: { label: 'PV Capex [$/kw-ac]', customUnit: '$' },
    pv_capex_dc: { label: 'PV Capex [$/kw-dc]', customUnit: '$' },
    pv_gen_mwh: { label: 'PV Gen [MWh]' },
    wind_capex: { label: 'Wind Capex [$]', customUnit: '$' },
    wind_gen_mwh: { label: 'Wind Gen [MWh]' },
    bess_capex_deprecated: { label: 'BESS Capex [$]', customUnit: '$' },
    ...fromObjectEntries(_.range(1, MAX_ESS_COUNT + 1).map((i) => [
        `ess_${i}_capex`,
        { label: `ESS ${i} Capex [$]`, customUnit: '$' },
    ])),
    ...fromObjectEntries(_.range(1, MAX_THERMAL_COUNT + 1).map((i) => [
        `thermal_${i}_capex`,
        { label: `Thermal ${i} Capex [$]`, customUnit: '$' },
    ])),
    pv_om_cost: { label: 'PV O&M Cost [$]', customUnit: '$' },
    wind_om_cost: { label: 'Wind O&M Cost [$]', customUnit: '$' },
    bess_om_cost_deprecated: { label: 'BESS O&M Cost [$]', customUnit: '$' },
    ...fromObjectEntries(_.range(1, MAX_ESS_COUNT + 1).map((i) => [
        `ess_${i}_om_cost`,
        { label: `ESS ${i} O&M Cost [$]`, customUnit: '$' },
    ])),
    ...fromObjectEntries(_.range(1, MAX_THERMAL_COUNT + 1).map((i) => [
        `thermal_${i}_fom_cost`,
        { label: `Thermal ${i} Capex FOM Cost [$]`, customUnit: '$' },
    ])),
    pv_ppa_cost: { label: 'PV PPA Cost [$]', customUnit: '$' },
    wind_ppa_cost: { label: 'Wind PPA Cost [$]', customUnit: '$' },
    bess_capacity_cost_deprecated: { label: 'BESS Capacity Cost [$]', customUnit: '$' },
    ...fromObjectEntries(_.range(1, MAX_ESS_COUNT + 1).map((i) => [
        `ess_${i}_capacity_cost`,
        { label: `ESS ${i} Capacity Cost [$]`, customUnit: '$' },
    ])),
    ...fromObjectEntries(_.range(1, MAX_THERMAL_COUNT + 1).map((i) => [
        `thermal_${i}_toll_cost`,
        { label: `Thermal ${i} Toll Cost [$]`, customUnit: '$' },
    ])),
    ...fromObjectEntries(_.range(1, MAX_THERMAL_COUNT + 1).map((i) => [
        `thermal_${i}_fuel_cost`,
        { label: `Thermal ${i} Fuel Cost [$]`, customUnit: '$' },
    ])),
    ...fromObjectEntries(_.range(1, MAX_THERMAL_COUNT + 1).map((i) => [
        `thermal_${i}_vom_cost`,
        { label: `Thermal ${i} VOM Cost [$]`, customUnit: '$' },
    ])),
    firm_gas_transport_cost: { label: 'Firm Gas Transport Cost [$]', customUnit: '$' },
    carbon_cost: { label: 'Carbon Cost [$]', customUnit: '$' },
    carbon_emissions: { label: 'Carbon Emissions [Tons]' },
    poi_charges: { label: 'Other TMX/IC Charges [$]', customUnit: '$' },
    poi_capex: { label: 'Other TXM/IC Capex [$]', customUnit: '$' },
    load_ppa_price_risk_spread: { label: 'Load PPA Price Risk Spread [%]', customUnit: '%' },
    load_lcoe_benchmark: { label: 'Load LCOE Benchmark [$/MWh]', customUnit: '$' },
    load_lcoe_premium_over_benchmark: {
        label: 'Load LCOE Premium Over Benchmark [$/MWh]',
        customUnit: '$',
    },
    project_lcoe_benchmark: { label: 'Project LCOE Benchmark [$/MWh]', customUnit: '$' },
    project_lcoe_premium_over_benchmark: {
        label: 'Project LCOE Premium Over Benchmark [$/MWh]',
        customUnit: '$',
    },
    pv_mvbus_clipping_mwh: { label: 'PV MV Bus Clipping [MWh]' },
    wind_mvbus_clipping_mwh: { label: 'Wind MV Bus Clipping [MWh]' },
};
export const plantSimDataColumnsLabelMap = {
    charge_1_yearly: { label: 'Charge 1 [MWh]' },
    charge_2_yearly: { label: 'Charge 2 [MWh]' },
    delivery_mwh_yearly: { label: 'Delivery [MWh]' },
    discharge_1_yearly: { label: 'Discharge 1 [MWh]' },
    discharge_2_yearly: { label: 'Discharge 2 [MWh]' },
    poi_clipping_mwh_yearly: { label: 'POI Clipping [MWh]' },
    poi_clipping_perc_yearly: { label: 'POI Clipping [%]', customUnit: '%' },
    pv_gen_mwh_yearly: { label: 'PV Gen [MWh]' },
    wind_gen_mwh_yearly: { label: 'Wind Gen [MWh]' },
    total_shape_yearly: { label: 'Total Shape [MWh]' },
    total_annual_load_match_yearly: {
        label: '(Total Annual Load Match) [%]',
        customUnit: '%',
    },
    annual_shape_match_yearly: { label: 'Annual Shape Match [%]', customUnit: '%' },
    shape_met_yearly: { label: 'Shape Met [%]', customUnit: '%' },
    full_shape_met_frequency_yearly: { label: 'Full Shape Met Frequency [%]', customUnit: '%' },
    deficit_yearly: { label: 'Deficit [%]', customUnit: '%' },
    excess_yearly: { label: 'Excess [%]', customUnit: '%' },
    load_ppa_price_yearly: { label: 'Load PPA Price [$/MWh]', customUnit: '$' },
    shape_adjusted_project_ppa_price_yearly: {
        label: 'Shape Adjusted Project PPA Price [$/MWh]',
        customUnit: '$',
    },
    ppa_price_yearly: { label: 'Project PPA Price [$/MWh]', customUnit: '$' },
};
export const netBenefitColumnsLabelToKeyMap = fromObjectEntries(objectEntries(netBenefitColumnsLabelMap).map(([key, data]) => [data.label, key]));
export const plantSimDataColumnsLabelToKeyMap = fromObjectEntries(objectEntries(plantSimDataColumnsLabelMap).map(([key, data]) => [data.label, key]));
export const plantSimTsTimeframes = [
    'raw',
    'first_yr',
    'monthly_sum',
    'monthly_avg',
    'annual_for_term_sum',
    'annual_for_term_avg',
    'avg_12x24',
    'sum_12x24',
    'std_12x24',
];
export const batchParamLabels = {
    poi_mw: 'POI MW',
    pv_n_inverters: 'PV Inverters',
    pv_ilr: 'PV ILR',
    ...fromObjectEntries(_.range(1, MAX_ESS_COUNT + 1).flatMap((i) => [
        [`ess_${i}_power`, `ESS ${i} Power`],
        [`ess_${i}_duration`, `ESS ${i} Duration`],
    ])),
    ...fromObjectEntries(_.range(1, MAX_THERMAL_COUNT + 1).flatMap((i) => [
        [`thermal_${i}_unit_count`, `Thermal ${i} Unit Count`],
        [`thermal_${i}_design_mw`, `Thermal ${i} Design MW`],
        [
            `thermal_${i}_annual_capacity_factor`,
            `Thermal ${i} Annual Capacity Factor %`,
        ],
        [
            `thermal_${i}_annual_energy_max`,
            `Thermal ${i} Annual Energy Max MWh/year`,
        ],
    ])),
    wind_n_turbines: 'Wind Turbines',
    shape_scale: 'Shape Scale',
    h2_electrolyzer_tpd: 'H2 Electrolyzer',
    h2_electrolyzer_storage: 'H2 Electrolyzer Storage',
    h2_liquefier_tpd: 'H2 Liquefier',
    h2_min_tpd: 'H2 Min',
    h2_max_tpd: 'H2 Max',
    h2_revenue: 'H2 Revenue',
};
export var Statistic;
(function (Statistic) {
    Statistic["MEAN"] = "mean";
    Statistic["P1"] = "p1";
    Statistic["P5"] = "p5";
    Statistic["P10"] = "p10";
    Statistic["P25"] = "p25";
    Statistic["P50"] = "p50";
    Statistic["P75"] = "p75";
    Statistic["P90"] = "p90";
    Statistic["P95"] = "p95";
    Statistic["P99"] = "p99";
})(Statistic || (Statistic = {}));
//# sourceMappingURL=plant-sim.js.map